export const WORK_PIPELINE_STAGES = ["proposed", "plan", "build", "review", "qa", "done"];
/** A work item needs a person exactly when an open gate exists on it. */
export function isNeedsYou(item) {
    return item.currentGateId != null;
}
/** A Fletcher verification item: its own request, standing in the QA stage. */
export function isFletcherRequestItem(item) {
    return (item.personaSlug === "fletcher" &&
        typeof item.sourceRef?.workerRequestId === "string" &&
        item.sourceRef.workerRequestId.length > 0);
}
/** The stage each kind of open ask belongs to. */
const GATE_STAGE = {
    accept_proposal: "proposed",
    approve_worker_instruction: "plan",
    approve_execution_plan: "plan",
    needs_input: "build",
    review_result: "review",
    publish_worker_review: "review",
};
function isTerminal(item) {
    return item.phase === "done" || item.phase === "dismissed" || item.phase === "split";
}
/**
 * A split whose tickets have not all merged (YAG-1111): the parent is live,
 * not closed, and stands where its tickets stand. Null for any other item,
 * and for a split whose tickets were not read here.
 */
export function liveSplitOf(item) {
    const progress = item.phase === "split" ? item.splitProgress : null;
    if (!progress || progress.total < 1 || progress.merged >= progress.total)
        return null;
    return progress;
}
function stageRank(stage) {
    return WORK_PIPELINE_STAGES.indexOf(stage);
}
/**
 * The stage a live split stands in: its least-advanced ticket that has not
 * merged. A ticket that closed without merging is passed over while another
 * is still moving.
 */
export function splitStageOf(progress) {
    const open = progress.parts.filter((part) => part.state !== "merged");
    const moving = open.filter((part) => part.state !== "closed");
    let stage = null;
    for (const part of moving.length > 0 ? moving : open) {
        if (stage === null || stageRank(part.stage) < stageRank(stage))
            stage = part.stage;
    }
    return stage ?? "build";
}
/**
 * The ticket a live split's row names: the first, in split order, that waits
 * on a person; else the first that is moving or stopped; else the first that
 * has not merged. Null when none of them was read.
 */
export function splitLeadOf(progress) {
    const open = progress.parts.filter((part) => part.state !== "merged");
    return (open.find((part) => part.state === "waiting") ??
        open.find((part) => part.state === "working" || part.state === "stopped") ??
        open[0] ??
        null);
}
/** Which pipeline stage one item stands in right now. */
export function pipelineStageOf(item) {
    // A live split stands where its tickets stand, unless it asks a person
    // itself (a ticket of it stopped short of its merge).
    const split = liveSplitOf(item);
    if (split && !isNeedsYou(item))
        return splitStageOf(split);
    if (isTerminal(item)) {
        if (!isNeedsYou(item))
            return "done";
        return GATE_STAGE[item.currentGateKind ?? ""] ?? "review";
    }
    if (isFletcherRequestItem(item))
        return "qa";
    if (item.kind === "review")
        return "review";
    if (isNeedsYou(item) && item.currentGateKind && item.currentGateKind !== "needs_input") {
        const gateStage = GATE_STAGE[item.currentGateKind];
        if (gateStage)
            return gateStage;
    }
    switch (item.phase) {
        case "proposed":
            return "proposed";
        case "accepted":
        case "planning":
        case "awaiting_execution_approval":
            return "plan";
        case "final_review":
            return "review";
        case "executing":
        case "needs_input":
            return item.prUrl ? "review" : "build";
        default:
            return "build";
    }
}
/**
 * How the item's stage came out: shipped (done), canceled (dismissed or
 * split, no work landed), or failed (its own review or QA verdict failed).
 * Null while it is still moving, for a terminal item that still carries an
 * open ask, which is not finished, and for a split whose tickets have not all
 * merged, which is live.
 */
export function stageOutcomeOf(item) {
    if (isTerminal(item) && !isNeedsYou(item) && !liveSplitOf(item)) {
        return item.phase === "done" ? "shipped" : "canceled";
    }
    if (item.stageVerdict === "failed")
        return "failed";
    return null;
}
function later(a, b) {
    if (!b)
        return a;
    return Date.parse(b) > Date.parse(a) ? b : a;
}
/**
 * When the item entered the stage it stands in now, from the first timestamp
 * that stage leaves behind. Never earlier than the item itself.
 */
export function stageEnteredAtOf(item, marks) {
    const born = item.createdAt;
    const pick = (...candidates) => {
        const found = candidates.find((c) => typeof c === "string" && c.length > 0);
        return later(born, found);
    };
    switch (pipelineStageOf(item)) {
        case "proposed":
            return pick(item.currentGateKind === "accept_proposal" ? item.currentGateOpenedAt : null);
        case "plan":
            return pick(marks.acceptResolvedAt);
        case "build":
            return pick(marks.planApprovedAt, item.latestRunStartedAt, marks.lastPhaseChangeAt, item.updatedAt);
        case "review":
            if (item.kind === "review")
                return born;
            return pick(marks.prOpenedAt, marks.lastPhaseChangeAt, item.updatedAt);
        case "qa":
            return born;
        case "done":
            return pick(marks.lastPhaseChangeAt, item.updatedAt);
    }
}
//# sourceMappingURL=pipelineStage.js.map